import { Group, Modal, Stack } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Button, Icon, Text } from '@repo/foundations';
import { useEffect, useState } from 'react';
import type { Monitor } from '../../api';
import { useBulkUpdateMonitors } from '../../api';
import type { ScheduleConfig } from '../../api/types/models/schedule';
import { ScheduleCadence } from '../../api/types/models/schedule';
import ScheduleSelector from '../../components/ScheduleSelector/ScheduleSelector';

interface BulkEditSchedulesModalProps {
	opened: boolean;
	onClose: () => void;
	monitors: Monitor[];
}

export default function BulkEditSchedulesModal({
	opened,
	onClose,
	monitors,
}: BulkEditSchedulesModalProps) {
	const { mutateAsync: bulkUpdateMonitors } = useBulkUpdateMonitors({});
	const [schedule, setSchedule] = useState<ScheduleConfig>({
		cadence: ScheduleCadence.DAILY,
		frequency: 1,
		hour: 0,
		day: 0,
	});
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		if (opened) {
			setSchedule({
				cadence: ScheduleCadence.DAILY,
				frequency: 1,
				hour: 0,
				day: 0,
			});
		}
	}, [opened]);

	const handleScheduleChange = (newSchedule: ScheduleConfig) => {
		setSchedule(newSchedule);
	};

	const handleSubmit = async () => {
		setSubmitting(true);
		try {
			// Build updates object for bulk update
			const updates: Record<string, Record<string, any>> = {};
			monitors.forEach((monitor) => {
				updates[monitor.id] = {
					schedule: schedule,
				};
			});

			await bulkUpdateMonitors({
				body: {
					updates,
				},
			});

			showNotification({
				title: 'Schedules updated',
				message: `Schedules updated for ${monitors.length} monitor${monitors.length > 1 ? 's' : ''}.`,
				color: 'green',
				icon: <Icon name="check" color="text/success-on-fill/default" />,
			});
			onClose();
		} catch (e) {
			showNotification({
				title: 'Error',
				message: 'Failed to update schedules for selected monitors.',
				color: 'red',
				icon: <Icon name="alertCircle" color="text/critical-on-fill/default" />,
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Edit Schedules"
			closeOnEscape
			closeOnClickOutside
			size="lg"
			maw={700}
		>
			<Stack spacing="lg">
				<Text size="sm" color="text/secondary/default">
					Update the schedule for all selected monitors. This will apply the
					same schedule configuration to all {monitors.length} monitor
					{monitors.length > 1 ? 's' : ''}.
				</Text>
				<ScheduleSelector
					schedule={schedule}
					onChange={handleScheduleChange}
					disableWeekly
					readOnly={submitting}
				/>
				<Group position="right" mt="md" spacing={8}>
					<Button variant="default" onClick={onClose} disabled={submitting}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handleSubmit} loading={submitting}>
						{`Update ${monitors.length} Monitor${monitors.length === 1 ? '' : 's'}`}
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
